'use client'
import {
  ContentfulRichText_default
} from "./chunk-DR2RGIIC.mjs";
import {
  DecoratedSection_default
} from "./chunk-HJCFOVNG.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/text/CollapsableText.tsx
import { useLayoutEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { twMerge } from "tailwind-merge";
import { Button } from "@wbk/ui";
import { useTranslation } from "@wbk/config";

// src/components/text/MarkdownRender.tsx
import Markdown from "markdown-to-jsx";
import { jsx } from "react/jsx-runtime";
var MarkdownRender = ({ text, prose = true }) => {
  return /* @__PURE__ */ jsx("div", { className: `w-full max-w-full ${prose ? "prose dark:prose-invert" : ""}`, children: /* @__PURE__ */ jsx(
    Markdown,
    {
      options: {
        forceBlock: true,
        overrides: {
          a: {
            component: "a",
            props: {
              className: "text-primary hover:underline"
            }
          },
          h2: {
            props: {
              className: "text-xl"
            }
          },
          p: {
            props: {
              className: "my-1"
            }
          },
          ul: {
            props: {
              className: "my-2"
            }
          },
          ol: {
            props: {
              className: "my-2"
            }
          }
        }
      },
      children: text
    }
  ) });
};
var MarkdownRender_default = MarkdownRender;

// src/components/text/CollapsableText.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var lineClamps = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
  12: "line-clamp-[12]",
  none: "line-clamp-none"
};
var CollapsableText = ({ text, minimum = 3, className, btnProps, markdown, children }) => {
  const { t } = useTranslation("contentful_common");
  const [showExpandBtn, setShowExpandBtn] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [height, setHeight] = useState("auto");
  const collapsedHeightRef = useRef("auto");
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const { clientHeight, scrollHeight } = ref.current;
    setShowExpandBtn(scrollHeight > clientHeight);
    setHeight(clientHeight);
    collapsedHeightRef.current = clientHeight;
  }, []);
  const toggle = () => {
    setIsExpanded(!isExpanded);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      setHeight(isExpanded ? collapsedHeightRef.current : ref.current.scrollHeight);
    });
  };
  if (!text && !children) return null;
  return /* @__PURE__ */ jsxs("div", { className: "relative inline-block w-full", children: [
    /* @__PURE__ */ jsx2(
      motion.div,
      {
        transition: { duration: 0.25, ease: "easeInOut" },
        className: "overflow-hidden",
        animate: { height },
        style: { height },
        children: /* @__PURE__ */ jsx2(
          "div",
          {
            ref,
            "data-testid": "collapsable-text",
            className: twMerge(
              "overflow-hidden",
              !isExpanded && lineClamps[minimum],
              markdown ? "text-text" : "text-text max-w-full whitespace-pre-wrap",
              (markdown || typeof text !== "string" && !children) && "prose w-full max-w-full",
              className
            ),
            children: children ? children : markdown ? /* @__PURE__ */ jsx2(MarkdownRender_default, { text, prose: false }) : typeof text === "string" ? /* @__PURE__ */ jsx2("div", { dangerouslySetInnerHTML: { __html: text } }) : /* @__PURE__ */ jsx2(ContentfulRichText_default, { description: text })
          }
        )
      }
    ),
    showExpandBtn && /* @__PURE__ */ jsx2(
      Button,
      __spreadProps(__spreadValues({
        onClick: toggle,
        shape: "text",
        theme: "transparent",
        className: "relative mt-2 px-0.5 py-0 text-sm underline ring-0 hover:underline",
        "aria-label": isExpanded ? t("contentful_common:read_less") : t("contentful_common:read_more")
      }, btnProps), {
        children: isExpanded ? t("contentful_common:read_less") : t("contentful_common:read_more")
      })
    )
  ] });
};
var CollapsableText_default = CollapsableText;

// src/components/text/CollapsableTextSection.tsx
import { Typography } from "@wbk/ui";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var CollapsableTextSection = ({ section }) => {
  var _a;
  if (!((_a = section.collapsableDescription) == null ? void 0 : _a.json)) {
    return null;
  }
  return /* @__PURE__ */ jsxs2(
    DecoratedSection_default,
    {
      section,
      className: "container overflow-hidden",
      "data-testid": `section_collapsable_text_${section.id}`,
      children: [
        section.title && /* @__PURE__ */ jsxs2("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx3(Typography, { as: "h2", variant: "heading-L", children: section.title }),
          section.subtitle && /* @__PURE__ */ jsx3(Typography, { color: "text-secondary", children: section.subtitle })
        ] }),
        /* @__PURE__ */ jsx3(
          CollapsableText_default,
          {
            text: section.collapsableDescription,
            minimum: 6,
            btnProps: { "aria-label": `Read More About ${section.title}` }
          }
        )
      ]
    }
  );
};
var CollapsableTextSection_default = CollapsableTextSection;

export {
  MarkdownRender_default,
  CollapsableText_default,
  CollapsableTextSection_default
};
//# sourceMappingURL=chunk-MAJWJHOB.mjs.map